/**
 * Surface 1 v1 hand-curated synthetic audit-chain records — 12 records
 * spanning the v1 four-frame authority model (TPO + patient-rights +
 * public-health-statute + irb-approval) across 5 operation types
 * (read / write / compute / query / patient-export) and the 5
 * synthetic holders defined in `synthetic-holders.ts`.
 *
 * Each record is a canonical `AuditRecord` from `@shn/substrate-types`
 * so the production rendering pipeline (`V0_RENDERING_TAXONOMY_ARTIFACT`
 * → patient-comprehensible narrative) consumes synthetic records via
 * the same code path as production per PR #210 §5.4 coherence test.
 *
 * Narratives produced by the v0 taxonomy on these records (target
 * audience: state Medicaid Directors viewing the 5-min walkthrough):
 * - TPO: "Atlantic Family Medicine viewed your records held at
 *   Atlantic Family Medicine for treatment, payment, or healthcare
 *   operations"
 * - Patient-rights: "You exported your records held at Atlantic Family
 *   Medicine in response to your patient-rights request"
 * - Public-health-statute: "Delmarva Public Health Network viewed
 *   your records held at Cape Henlopen Hospital as required by public
 *   health law"
 * - IRB-approval: "Patapsco Diagnostic Lab viewed your records held
 *   at Atlantic Family Medicine under an approved research protocol"
 *
 * **No real PHI at any point.** The synthetic patient PCI
 * (`SYNTHETIC_PATIENT_PCI`) is a placeholder string cast to the `PCI`
 * brand; cleartext PCI never appears at the substrate layer in
 * production but the demo is read-only synthetic so this is the
 * only narrative-coherent shape.
 */
/**
 * Synthetic patient subject PCI shared by all demo records — the
 * walkthrough demonstrates a single demo persona's projection. The
 * narrative overlay (CTO + content-collaborator territory) introduces
 * the persona by name; the data layer carries the canonical
 * structured PCI shape so production code paths consume synthetic
 * records without `as never`-style type bypass.
 *
 * Synthetic public-key payload is a placeholder string; demo
 * deployments don't perform PCI signature verification (Surface 1 is
 * read-only synthetic data; no crypto-active demo per PR #210 §5.5).
 * Issuing authority + issuedAt are framed against the synthetic
 * SHDT-demo persona so audit-record narratives are internally
 * consistent.
 */
const SYNTHETIC_PATIENT_PCI = Object.freeze({
    id: 'pci_demo_patient_v1',
    publicKey: 'demo-synthetic-public-key-no-real-crypto',
    issuedAt: '2026-04-01T00:00:00Z',
    issuingAuthority: 'shdt-demo-issuing-authority',
});
/** Helper — minimal `OperationalScope` for demo records. */
const DEMO_SCOPE = Object.freeze({
    dataScope: Object.freeze([]),
    outputCategories: Object.freeze([]),
});
export const SYNTHETIC_AUDIT_CHAIN_RECORDS = Object.freeze([
    // ---------- TPO records (6) — most common substrate operation ----------
    {
        v1Category: 'tpo',
        auditRecord: {
            recordId: 'audit_demo_001',
            operationId: 'op_demo_001',
            operationType: 'read',
            initiator: {
                type: 'holder',
                holderId: 'holder_atlantic_family_medicine',
                holderName: 'Atlantic Family Medicine',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'tpo',
                    authorityBasisId: 'auth_tpo_demo_001',
                    assertedBy: 'holder_atlantic_family_medicine',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-04-15T13:30:00Z',
            recordedAt: '2026-04-15T13:30:01Z',
        },
    },
    {
        v1Category: 'tpo',
        auditRecord: {
            recordId: 'audit_demo_002',
            operationId: 'op_demo_002',
            operationType: 'read',
            initiator: {
                type: 'holder',
                holderId: 'holder_cape_henlopen_hospital',
                holderName: 'Cape Henlopen Hospital',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'tpo',
                    authorityBasisId: 'auth_tpo_demo_002',
                    assertedBy: 'holder_cape_henlopen_hospital',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-04-22T09:15:00Z',
            recordedAt: '2026-04-22T09:15:01Z',
        },
    },
    {
        v1Category: 'tpo',
        auditRecord: {
            recordId: 'audit_demo_003',
            operationId: 'op_demo_003',
            operationType: 'write',
            initiator: {
                type: 'holder',
                holderId: 'holder_patapsco_lab',
                holderName: 'Patapsco Diagnostic Lab',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'tpo',
                    authorityBasisId: 'auth_tpo_demo_003',
                    assertedBy: 'holder_patapsco_lab',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-04-25T10:42:00Z',
            recordedAt: '2026-04-25T10:42:01Z',
        },
    },
    {
        v1Category: 'tpo',
        auditRecord: {
            recordId: 'audit_demo_004',
            operationId: 'op_demo_004',
            operationType: 'read',
            initiator: {
                type: 'holder',
                holderId: 'holder_granite_state_pharmacy',
                holderName: 'Granite State Specialty Pharmacy',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'tpo',
                    authorityBasisId: 'auth_tpo_demo_004',
                    assertedBy: 'holder_granite_state_pharmacy',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-04-28T14:08:00Z',
            recordedAt: '2026-04-28T14:08:01Z',
        },
    },
    {
        v1Category: 'tpo',
        auditRecord: {
            recordId: 'audit_demo_005',
            operationId: 'op_demo_005',
            operationType: 'write',
            initiator: {
                type: 'holder',
                holderId: 'holder_cape_henlopen_hospital',
                holderName: 'Cape Henlopen Hospital',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'tpo',
                    authorityBasisId: 'auth_tpo_demo_005',
                    assertedBy: 'holder_cape_henlopen_hospital',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Cape Henlopen Hospital'],
            signatures: [],
            initiatedAt: '2026-05-01T18:22:00Z',
            recordedAt: '2026-05-01T18:22:01Z',
        },
    },
    {
        v1Category: 'tpo',
        auditRecord: {
            recordId: 'audit_demo_006',
            operationId: 'op_demo_006',
            operationType: 'read',
            initiator: {
                type: 'holder',
                holderId: 'holder_atlantic_family_medicine',
                holderName: 'Atlantic Family Medicine',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'tpo',
                    authorityBasisId: 'auth_tpo_demo_006',
                    assertedBy: 'holder_atlantic_family_medicine',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Cape Henlopen Hospital', 'Patapsco Diagnostic Lab'],
            signatures: [],
            initiatedAt: '2026-05-03T11:05:00Z',
            recordedAt: '2026-05-03T11:05:01Z',
        },
    },
    // ---------- Patient-rights records (2) — patient-initiated ----------
    {
        v1Category: 'patient-rights',
        auditRecord: {
            recordId: 'audit_demo_007',
            operationId: 'op_demo_007',
            operationType: 'patient-export',
            initiator: { type: 'patient', pci: SYNTHETIC_PATIENT_PCI },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'patient-rights',
                    authorityBasisId: 'auth_patient_rights_demo_001',
                    assertedBy: 'patient',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-05-02T16:10:00Z',
            recordedAt: '2026-05-02T16:10:01Z',
        },
    },
    {
        v1Category: 'patient-rights',
        auditRecord: {
            recordId: 'audit_demo_008',
            operationId: 'op_demo_008',
            operationType: 'consent-revocation',
            initiator: { type: 'patient', pci: SYNTHETIC_PATIENT_PCI },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'patient-rights',
                    authorityBasisId: 'auth_patient_rights_demo_002',
                    assertedBy: 'patient',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: [],
            signatures: [],
            initiatedAt: '2026-05-04T09:48:00Z',
            recordedAt: '2026-05-04T09:48:01Z',
        },
    },
    // ---------- Public-health-statute records (2) ----------
    {
        v1Category: 'public-health-statute',
        auditRecord: {
            recordId: 'audit_demo_009',
            operationId: 'op_demo_009',
            operationType: 'read',
            initiator: {
                type: 'holder',
                holderId: 'holder_delmarva_public_health',
                holderName: 'Delmarva Public Health Network',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'public-health-statute',
                    authorityBasisId: 'auth_phs_demo_001',
                    assertedBy: 'holder_delmarva_public_health',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Cape Henlopen Hospital'],
            signatures: [],
            initiatedAt: '2026-04-29T07:30:00Z',
            recordedAt: '2026-04-29T07:30:01Z',
        },
    },
    {
        v1Category: 'public-health-statute',
        auditRecord: {
            recordId: 'audit_demo_010',
            operationId: 'op_demo_010',
            operationType: 'query',
            initiator: {
                type: 'holder',
                holderId: 'holder_delmarva_public_health',
                holderName: 'Delmarva Public Health Network',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'public-health-statute',
                    authorityBasisId: 'auth_phs_demo_002',
                    assertedBy: 'holder_delmarva_public_health',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Patapsco Diagnostic Lab'],
            signatures: [],
            initiatedAt: '2026-05-01T08:00:00Z',
            recordedAt: '2026-05-01T08:00:01Z',
        },
    },
    // ---------- IRB-approval records (2) ----------
    {
        v1Category: 'irb-approval',
        auditRecord: {
            recordId: 'audit_demo_011',
            operationId: 'op_demo_011',
            operationType: 'read',
            initiator: {
                type: 'holder',
                holderId: 'holder_patapsco_lab',
                holderName: 'Patapsco Diagnostic Lab',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'irb-approval',
                    authorityBasisId: 'auth_irb_demo_001',
                    assertedBy: 'holder_patapsco_lab',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-04-30T13:15:00Z',
            recordedAt: '2026-04-30T13:15:01Z',
        },
    },
    {
        v1Category: 'irb-approval',
        auditRecord: {
            recordId: 'audit_demo_012',
            operationId: 'op_demo_012',
            operationType: 'compute',
            initiator: {
                type: 'holder',
                holderId: 'holder_patapsco_lab',
                holderName: 'Patapsco Diagnostic Lab',
            },
            subject: SYNTHETIC_PATIENT_PCI,
            assertedAuthority: [
                {
                    category: 'irb-approval',
                    authorityBasisId: 'auth_irb_demo_002',
                    assertedBy: 'holder_patapsco_lab',
                    metadata: {},
                },
            ],
            scope: DEMO_SCOPE,
            authorizationOutcome: 'authorized',
            targetHolders: ['Atlantic Family Medicine'],
            signatures: [],
            initiatedAt: '2026-05-02T11:30:00Z',
            recordedAt: '2026-05-02T11:30:01Z',
        },
    },
]);
//# sourceMappingURL=synthetic-audit-records.js.map